import { useState, useEffect, useCallback } from "react";
import { Menu, X, Printer, ExternalLink } from "lucide-react";
import { SECTION_NAMES } from "./data/presentationData";
import { Section1 } from "./components/presentation/Section1";
import { Section2 } from "./components/presentation/Section2";
import { Section3 } from "./components/presentation/Section3";
import { Section4 } from "./components/presentation/Section4";
import { Section5 } from "./components/presentation/Section5";
import { Section6 } from "./components/presentation/Section6";
import { Section7 } from "./components/presentation/Section7";
import { Section8 } from "./components/presentation/Section8";
import { Section9 } from "./components/presentation/Section9";
import "./App.css";

const SECTION_HASHES = ["opportunity", "fsu-path", "gap", "blueprint", "projects", "alignment", "network", "platform", "ask"];

export default function App() {
  const [cur, setCur] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const scrollTo = useCallback((i) => {
    const el = document.getElementById(`section-${i + 1}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setCur(i);
      window.history.replaceState(null, "", `#${SECTION_HASHES[i]}`);
    }
    setMobileOpen(false);
  }, []);

  // Hash deep-linking on load
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const idx = SECTION_HASHES.indexOf(hash);
      if (idx >= 0) setTimeout(() => scrollTo(idx), 300);
    }
  }, [scrollTo]);

  // Keyboard navigation
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") { e.preventDefault(); scrollTo(Math.min(cur + 1, 8)); }
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") { e.preventDefault(); scrollTo(Math.max(cur - 1, 0)); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [cur, scrollTo]);

  // Scroll observer for current section tracking + hash update
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const idx = parseInt(e.target.id.split("-")[1]) - 1;
          if (!isNaN(idx)) {
            setCur(idx);
            window.history.replaceState(null, "", `#${SECTION_HASHES[idx]}`);
          }
        }
      });
    }, { threshold: 0.35 });
    for (let i = 1; i <= 9; i++) { const el = document.getElementById(`section-${i}`); if (el) obs.observe(el); }
    return () => obs.disconnect();
  }, []);

  return (
    <div className="relative" data-testid="presentation-app">
      {/* Skip to content for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-[#005BBB] focus:text-white focus:px-4 focus:py-2 focus:rounded">Skip to content</a>

      {/* ═══════ TOP NAV ═══════ */}
      <header>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0c1222]/95 backdrop-blur-md border-b border-slate-800/80 print-hide" data-testid="main-navigation" aria-label="Main navigation">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-4">
            {/* Brand */}
            <a href="https://artofthediia.org" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group" data-testid="brand-link">
              <span className="brand-dot" />
              <span className="font-bold text-white text-sm group-hover:text-[#C5A258] transition" style={{ fontFamily: 'var(--font-display)' }}>artofthediia<span className="text-[#C5A258]">.org</span></span>
            </a>
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-0.5">
              {SECTION_NAMES.map((name, i) => (
                <button key={i} data-testid={`nav-${name.toLowerCase().replace(/\s/g, '-')}`} onClick={() => scrollTo(i)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs transition ${cur === i ? "bg-[#005BBB] text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/50"}`} style={{ fontFamily: 'var(--font-body)' }}>{name}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="print-btn" onClick={() => window.print()} className="text-slate-500 hover:text-white transition hidden sm:flex items-center gap-1.5 text-[10px]" style={{ fontFamily: 'var(--font-body)' }} title="Print / Save as PDF">
              <Printer className="w-3.5 h-3.5" /><span className="hidden lg:inline">Export PDF</span>
            </button>
            <span className="text-[10px] text-slate-500 hidden lg:block" style={{ fontFamily: 'var(--font-body)' }}>Arrow keys to navigate</span>
            {/* Mobile hamburger */}
            <button data-testid="hamburger-btn" onClick={() => setMobileOpen(!mobileOpen)} className={`md:hidden p-1.5 rounded-lg transition ${mobileOpen ? "bg-slate-700 hamburger-open" : "hover:bg-slate-800"}`}>
              <div className="w-5 flex flex-col gap-[5px]">
                <div className="hamburger-line h-[2px] w-full bg-white rounded-full" />
                <div className="hamburger-line h-[2px] w-full bg-white rounded-full" />
                <div className="hamburger-line h-[2px] w-full bg-white rounded-full" />
              </div>
            </button>
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-[2px] bg-slate-800"><div className="h-full bg-[#005BBB] progress-bar" style={{ width: `${((cur + 1) / 9) * 100}%` }} /></div>
      </nav>
      </header>

      {/* ═══════ MOBILE MENU ═══════ */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[45] bg-[#0c1222]/98 backdrop-blur-xl pt-16 mobile-menu-enter md:hidden print-hide" data-testid="mobile-menu">
          <div className="flex flex-col items-center gap-1 p-6">
            {SECTION_NAMES.map((name, i) => (
              <button key={i} data-testid={`mobile-nav-${SECTION_HASHES[i]}`} onClick={() => scrollTo(i)}
                className={`w-full max-w-xs px-5 py-3.5 rounded-xl text-sm font-medium transition ${cur === i ? "bg-[#005BBB] text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/60"}`} style={{ fontFamily: 'var(--font-body)' }}>
                <span className="text-[10px] text-slate-500 mr-2">{String(i + 1).padStart(2, '0')}</span>{name}
              </button>
            ))}
            <div className="mt-6 pt-6 border-t border-slate-800 w-full max-w-xs text-center">
              <a href="https://artofthediia.org" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-[#C5A258] transition flex items-center justify-center gap-1" style={{ fontFamily: 'var(--font-body)' }}>
                artofthediia.org <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ═══════ SIDE DOTS ═══════ */}
      <div className="fixed right-5 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-2.5 print-hide">
        {SECTION_NAMES.map((name, i) => (
          <button key={i} onClick={() => scrollTo(i)} className="group flex items-center gap-2.5" title={name}>
            <span className="hidden group-hover:block text-[10px] text-white bg-slate-800 rounded-md px-2.5 py-1 whitespace-nowrap" style={{ fontFamily: 'var(--font-body)' }}>{name}</span>
            <div className={`w-2 h-2 rounded-full transition-all ${cur === i ? "bg-[#005BBB] scale-150" : "bg-slate-600 hover:bg-slate-400"}`} />
          </button>
        ))}
      </div>

      {/* ═══════ SECTIONS ═══════ */}
      <main id="main-content" className="pt-12" role="main">
        <Section1 />
        <div className="section-divider" />
        <Section2 />
        <div className="section-divider" />
        <Section3 />
        <div className="section-divider" />
        <Section4 />
        <div className="section-divider" />
        <Section5 />
        <div className="section-divider" />
        <Section6 />
        <div className="section-divider" />
        <Section7 />
        <div className="section-divider" />
        <Section8 />
        <div className="section-divider" />
        <Section9 />
      </main>

      {/* ═══════ PRINT FOOTER ═══════ */}
      <footer className="print-footer text-center py-6 border-t border-slate-200">
        <p className="text-xs text-slate-500" style={{ fontFamily: 'var(--font-body)' }}>artofthediia.org &middot; A Research Proposal by Matthew R. MacFarlane</p>
      </footer>
    </div>
  );
}
